import { Textarea as MuiTextarea } from '@mui/joy';
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRightIcon } from 'tdesign-icons-react';
import { Button, MessagePlugin, Tabs, Tag, Tooltip } from 'tdesign-react';
import TabPanel from 'tdesign-react/es/tabs/TabPanel';

import { Star2 } from '@/components/icon';
import { labelDom, LabelDomProps } from '@/components/Label';
import { BasicImageViewer } from '@/components/review/image';
import { ImageRatio } from '@/const/richMedia';
import { PageIds } from '@/const/tip';
import { AiImageReferenceType, AiImageReferenceTypeLabel, AiPrompt, AiType, PolishPromptReq, PolishPromptResp } from '@/interface/aigc';
import { ImageDomType, RichMediaValue } from '@/interface/media';
import UploadPic from '@/material/form-upload/image';
import { polishPrompt } from '@/services/aigc';
import { CgiErrorCode, CgiResp } from '@/services/cgi';
import { useTypedSelector } from '@/store';
import { getUnicodeLength } from '@/utils/getUnicodeLength';

import PolishPromptDialog from '../TextareaPrompt/dialog';
import ReferenceDialog from './dialog';
import styles from './index.module.less';

interface IProps {
  onChange: (key: AiPrompt) => void;
  value: AiPrompt;
  polish: boolean;
  fieldname: string;
  labelSetting: {
    tipIcon: boolean;
    tipText: string;
  };
  type: AiType;
  referOptions?: AiImageReferenceType[]; // 支持的参考类型
  showUpload?: boolean; // 是否显示上传按钮
  disabledUpload?: boolean; // 是否禁用上传按钮
  hideReferDialog?: boolean; // 是否隐藏参考对话框

  // Textarea属性
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  maxlength?: number;
  autosize?: {
    minRows: number;
    maxRows: number;
  }
}

const TextareaImage: React.FC<IProps> = ({
  onChange,
  value,
  polish,
  labelSetting,
  type,
  disabled,
  referOptions,
  showUpload = true,
  disabledUpload = false,
  hideReferDialog = false,
  ...props
}) => {
  const [prompt, setPrompt] = useState<string>(value?.prompt || '');
  const [newPrompt, setNewPrompt] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [polishVisible, setPolishVisible] = useState<boolean>(false);
  const [referVisible, setReferVisible] = useState<boolean>(false);
  const maxlength = props.maxlength || 800;

  const tipList = useTypedSelector(store => store.tip);
  const { fixedVal = '', category = '' } = useTypedSelector(store => store.tag)
    .find((e: any) => e.type === type)
    || { fixedVal: '', category: '' };
  const words = fixedVal.split('、');
  const panels: { name: string, tags: string }[] = useMemo(() => {
    try {
      return JSON.parse(category) || [];
    } catch {
      return [];
    }
  }, [category]);

  useEffect(() => {
    onChange({ ...value, prompt });
  }, [prompt]);

  const clickPolish = async () => {
    if (disabled) return;

    if (prompt.length === 0 || prompt.length > maxlength) {
      MessagePlugin.error({
        content: prompt.length === 0 ? '请填写画面描述' : `最多输入${maxlength}字`,
      });
      return;
    }

    setPolishVisible(false);
    setLoading(true);
    const promptReq: PolishPromptReq = { text: prompt };
    const { code, data, message } = await polishPrompt(promptReq) as CgiResp<PolishPromptResp>;
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error({
        content: message || '润色失败，请点击重试',
      });
      setLoading(false);
      return;
    }

    setNewPrompt(data.polishedText);
    setLoading(false);
    setPolishVisible(true);
  };

  const handlePolishConfirm = (confirmed: boolean) => {
    setPolishVisible(false);
    if (confirmed) setPrompt(newPrompt);
  };

  const handlePicChange = (image?: RichMediaValue) => {
    if (!image?.url?.url) {
      onChange({ ...value, image: {}, referenceType: AiImageReferenceType.DEFAULT });
    } else {
      // 如果隐藏参考对话框（比如4.0模型），直接设置为智能参考
      if (hideReferDialog) {
        onChange({ ...value, image, referenceType: AiImageReferenceType.AUTO });
      } else {
        setReferVisible(true);
        onChange({ ...value, image });
      }
    }
  };

  const handleReferConfirm = (val: AiPrompt) => {
    setReferVisible(false);
    onChange(val);
  };

  const clickWord = (word: string) => {
    if (disabled) return;

    const newPrompt = prompt ? `${prompt},${word}` : word;
    setPrompt(newPrompt.substring(0, maxlength));
  };

  const labelProps: LabelDomProps = {
    field: { labelSetting, props: props.fieldname, ...props },
    initFormData: '',
    requiredItem: props.required,
    pageId: PageIds.AIGC_INFO,
    tipList,
  };

  const getWordsDom = (words: string[]) => <div className={styles.wordsList}>
    {words.map((word, index) => (
      <div
        key={index}
        className={styles.word}
        onClick={() => clickWord(word)}
      >
        {word}
      </div>
    ))}
  </div>;

  return (
    <div className={styles.textWrapper}>
      {labelDom(labelProps)}
      <MuiTextarea
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        className={styles.muiTextarea}
        minRows={props.autosize?.minRows || 10}
        maxRows={props.autosize?.maxRows || 10}
        placeholder={props.placeholder || '请输入'}
        onKeyDown={(e) => {
          const target = e.target as HTMLTextAreaElement;
          if (e.key === 'Backspace'
            && target.selectionStart === 0
            && target.selectionEnd === 0
            && value?.image?.url?.url) {
            handlePicChange();
            e.preventDefault();
          }
        }}
        startDecorator={value?.image?.url?.url
          && <Tag
            closable
          disabled={disabled}
            className={styles.customTag}
            style={{ height: 26, minWidth: 0, maxWidth: '100%', padding: '0 6px 0 3px' }}
          onClose={() => handlePicChange()}
            content={
              <div className={styles.startDecorator}>
                <BasicImageViewer
                  hover={false}
                  styles={{ background: 'none', height: '100%' }}
                  imageStyles={{ width: 20, height: 20 }}
                  images={[value?.image?.url?.url || '']}
                  showImage={true}
                />
                <div
                  className={styles.imgName}
                  onClick={() => {
                    if (disabled || hideReferDialog) return;
                    setReferVisible(true);
                  }}
                  style={{ cursor: hideReferDialog ? 'default' : 'pointer' }}
                >
                  {AiImageReferenceTypeLabel[value.referenceType || AiImageReferenceType.AUTO]}
                </div>
              </div>}
          />
        }
        endDecorator={
          <div className={styles.endDecorator}>
            <div
              className={styles.btnGroup}
            >
              {showUpload && <UploadPic
                value={value?.image || {}}
                onChange={handlePicChange}
                limit={{
                  ratio: ImageRatio.unlimited,
                  fileType: ['jpg', 'jpeg', 'png', 'webp'],
                }}
                disabled={disabled || disabledUpload}
                domType={ImageDomType.REF}
              />}
              {polish && <Button
                disabled={disabled}
                variant="text"
                loading={loading}
                className={styles.button}
                onMouseDown={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={clickPolish}
              >
                {<div className={styles.text}>
                  {loading ? '润色中' : <><Star2 />描述润色</>}
                </div>}
              </Button>}
            </div>
            <div className={styles.limit}>
              {getUnicodeLength(prompt)}/{maxlength}
            </div>
          </div>
        }
      />
      {words && <div className={styles.wordsContainer}>
        {getWordsDom(words)}
        <Tooltip
          placement='right-top'
          trigger='click'
          content={<>
            <Tabs
              placement={'top'}
              style={{ background: 'transparent', paddingBottom: 10 }}
              className={styles.customTabs}
              defaultValue={0}
            >
              {panels.map(({ name, tags }, index) => (
                <TabPanel key={index} value={index} label={name}>
                  {getWordsDom(tags.split('、'))}
                </TabPanel>
              ))}
            </Tabs>
          </>}
        >
          <Button
            theme='default'
            shape='square'
            style={{ width: 60, height: 28, flexShrink: 0 }}
          >
            <div className={styles.moreBtn}>
              <span>更多</span>
              <ChevronRightIcon size={16} />
            </div>
          </Button>
        </Tooltip>
      </div>}

      {/* 润色弹窗 */}
      <PolishPromptDialog
        newPrompt={newPrompt}
        visible={polishVisible}
        onClose={() => setPolishVisible(false)}
        onConfirm={handlePolishConfirm}
        handleGenerate={clickPolish}
      />

      {/* 参考图片弹窗 */}
      <ReferenceDialog
        value={value}
        visible={!hideReferDialog && referVisible}
        options={referOptions}
        onClose={() => setReferVisible(false)}
        onConfirm={handleReferConfirm}
      />
    </div >
  );
};

export default TextareaImage;
